// Bound a pasted image before it enters the conversation.
//
// A pasted image is not just a render artifact: it goes into
// `agent.state.messages` and is re-sent with EVERY subsequent request for the
// life of the chat. That makes an oversized one uniquely damaging - the
// provider rejects the whole request, and because the image is now history,
// every later send fails the same way. One paste bricks the chat, and the only
// escape is rewinding past it.
//
// So the budget is enforced here, at the door, rather than at send time:
//   1. bound the long edge (a screenshot's pixels past this buy nothing - vision
//      models downscale large images anyway, so the extra only costs tokens),
//   2. re-encode down a quality ladder until it fits a byte budget,
//   3. refuse anything still over budget, so it never reaches history.
//
// The decisions are pure and unit-tested; only `prepareImage` touches canvas.

// Long edge, in pixels, an image is reduced to. Comfortably above what a vision
// model resolves while cutting a Retina screenshot to a fraction of its bytes.
export const MAX_EDGE_PX = 1568;

// Byte budget for the ENCODED image (pre-base64). Deliberately far below the
// ~10 MB base64 ceiling the provider enforced on us: base64 inflates by ~4/3,
// several images can share one request alongside the text, and each image also
// consumes the per-chat blob budget once persisted.
export const MAX_ENCODED_BYTES = 1_500_000;

// Re-encode quality steps, tried in order. Stops at the first that fits, so a
// small screenshot keeps the top quality.
export const QUALITY_LADDER = [0.92, 0.82, 0.7, 0.55, 0.4];

// Formats we re-encode INTO. PNG is kept for images that may carry
// transparency, where a JPEG matte would be a visible change rather than a
// compression artifact.
const JPEG = "image/jpeg";
const PNG = "image/png";

/// Dimensions an image is reduced to, preserving aspect ratio.
///
/// Never scales UP: a small screenshot is left exactly as it is, so this can't
/// turn a crisp 800px capture into a blurry 1568px one.
export function targetDimensions(width, height, maxEdge = MAX_EDGE_PX) {
    const w = Math.max(0, Math.round(width) || 0);
    const h = Math.max(0, Math.round(height) || 0);
    if (!w || !h) return { width: w, height: h, scaled: false };
    const longest = Math.max(w, h);
    if (longest <= maxEdge) return { width: w, height: h, scaled: false };
    const ratio = maxEdge / longest;
    return {
        width: Math.max(1, Math.round(w * ratio)),
        height: Math.max(1, Math.round(h * ratio)),
        scaled: true,
    };
}

/// Which format to re-encode into.
///
/// Alpha-capable sources stay PNG so a transparent background doesn't become a
/// black box; everything else becomes JPEG, which is what actually makes a
/// screenshot small.
export function encodeFormat(sourceMimeType) {
    const type = String(sourceMimeType || "").toLowerCase();
    return type === PNG || type === "image/webp" || type === "image/gif" ? PNG : JPEG;
}

/// Whether an already-small image can skip re-encoding entirely.
///
/// Re-encoding is lossy, so an image that is already inside both bounds is
/// passed through untouched - the common case for a cropped screenshot.
export function needsWork(width, height, encodedBytes, {
    maxEdge = MAX_EDGE_PX,
    maxBytes = MAX_ENCODED_BYTES,
} = {}) {
    return targetDimensions(width, height, maxEdge).scaled || encodedBytes > maxBytes;
}

/// Human-readable size, for the message shown when an image is refused.
export function formatBytes(bytes) {
    const mb = bytes / 1_048_576;
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;
}

/// Thrown when an image cannot be brought inside the budget. Carries the sizes
/// so the caller can tell the user what actually happened rather than "failed".
export class ImageTooLargeError extends Error {
    constructor(encodedBytes, maxBytes) {
        super(
            `This image is ${formatBytes(encodedBytes)} even after resizing, over the `
            + `${formatBytes(maxBytes)} limit. Try cropping it or sending a smaller capture.`,
        );
        this.name = "ImageTooLargeError";
        this.encodedBytes = encodedBytes;
        this.maxBytes = maxBytes;
    }
}

function dataUrlToParts(url) {
    const m = /^data:([^;]+);base64,(.*)$/.exec(String(url));
    if (!m) throw new Error("unrecognised image encoding");
    return { mimeType: m[1], data: m[2] };
}

// Approximate decoded byte length of a base64 payload without allocating it.
function base64Bytes(data) {
    const padding = data.endsWith("==") ? 2 : data.endsWith("=") ? 1 : 0;
    return Math.max(0, Math.floor((data.length * 3) / 4) - padding);
}

/// Bound one image for upload.
///
/// Returns the staged shape ChatConsole uses (`{ id, data, mimeType,
/// previewUrl }`) plus `originalBytes` / `encodedBytes` so a caller can report
/// what it did. Throws [`ImageTooLargeError`] when the ladder bottoms out - the
/// caller MUST surface that rather than staging the image, since an oversized
/// image in history is unrecoverable without a rewind.
///
/// `deps` exists so tests can drive the pure path without a real canvas.
export async function prepareImage(file, {
    maxEdge = MAX_EDGE_PX,
    maxBytes = MAX_ENCODED_BYTES,
    ladder = QUALITY_LADDER,
    deps,
} = {}) {
    const readDataUrl = deps?.readDataUrl ?? defaultReadDataUrl;
    const decode = deps?.decode ?? defaultDecode;
    const encode = deps?.encode ?? defaultEncode;

    const originalUrl = await readDataUrl(file);
    const original = dataUrlToParts(originalUrl);
    const originalBytes = base64Bytes(original.data);

    const bitmap = await decode(file, originalUrl);
    const { width, height } = bitmap;

    if (!needsWork(width, height, originalBytes, { maxEdge, maxBytes })) {
        return {
            id: stagedId(),
            data: original.data,
            mimeType: original.mimeType,
            previewUrl: originalUrl,
            originalBytes,
            encodedBytes: originalBytes,
            resized: false,
        };
    }

    const target = targetDimensions(width, height, maxEdge);
    const format = encodeFormat(original.mimeType);
    let smallest = null;
    for (const quality of format === PNG ? [undefined] : ladder) {
        const url = await encode(bitmap, target, format, quality);
        const parts = dataUrlToParts(url);
        const bytes = base64Bytes(parts.data);
        if (!smallest || bytes < smallest.bytes) smallest = { url, parts, bytes };
        if (bytes <= maxBytes) break;
    }

    // A PNG that is still too big re-encodes as JPEG rather than being refused:
    // losing transparency beats losing the image.
    if (smallest && smallest.bytes > maxBytes && format === PNG) {
        for (const quality of ladder) {
            const url = await encode(bitmap, target, JPEG, quality);
            const parts = dataUrlToParts(url);
            const bytes = base64Bytes(parts.data);
            if (bytes < smallest.bytes) smallest = { url, parts, bytes };
            if (bytes <= maxBytes) break;
        }
    }

    if (!smallest || smallest.bytes > maxBytes) {
        throw new ImageTooLargeError(smallest ? smallest.bytes : originalBytes, maxBytes);
    }
    return {
        id: stagedId(),
        data: smallest.parts.data,
        mimeType: smallest.parts.mimeType,
        previewUrl: smallest.url,
        originalBytes,
        encodedBytes: smallest.bytes,
        resized: true,
    };
}

function stagedId() {
    return `img:${Date.now()}:${Math.random().toString(36).slice(2, 7)}`;
}

function defaultReadDataUrl(file) {
    return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(fr.error || new Error("read failed"));
        fr.readAsDataURL(file);
    });
}

async function defaultDecode(file, dataUrl) {
    if (typeof createImageBitmap === "function" && typeof Blob !== "undefined" && file instanceof Blob) {
        return createImageBitmap(file);
    }
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error("could not decode image"));
        img.src = dataUrl;
    });
}

async function defaultEncode(bitmap, { width, height }, mimeType, quality) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    // White matte before a JPEG draw: JPEG has no alpha, and an untouched
    // canvas is transparent black, which would turn a transparent background
    // into a black one.
    if (mimeType === JPEG) {
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, width, height);
    }
    ctx.drawImage(bitmap, 0, 0, width, height);
    return canvas.toDataURL(mimeType, quality);
}
